import { apiClient } from '../client/apiClient';
import type {
	IBaseResponse,
	IPaginatedResponse,
	IPagination,
} from '../types/common';
import type {
	IChat,
	IChatMessage,
	IComment,
	ICommentPayload,
	IConversation,
	IFeedback,
	IFollowUp,
	IReaction,
} from '../types/conversation';

export const conversationService = {
	/**
	 * Search conversations
	 */
	searchConversations: (
		params: IPagination & {
			term?: string;
			category?: string;
			tag?: string;
			userId?: string;
		},
	) =>
		apiClient.get<IPaginatedResponse<IConversation>>('/conversation/search', {
			params,
		}),

	/**
	 * Get trending conversations
	 */
	getTrending: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>('/conversation/trending', {
			params,
		}),

	/**
	 * Get rising conversations
	 */
	getRising: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>('/conversation/rising', {
			params,
		}),

	/**
	 * Get new conversations
	 */
	getNew: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>('/conversation/new', {
			params,
		}),

	/**
	 * Get most viewed conversations
	 */
	getMostViewed: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>(
			'/conversation/most-view',
			{ params },
		),

	/**
	 * Get personalized trending conversations
	 */
	getForYouTrending: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>(
			'/conversation/for-you/trending',
			{ params },
		),

	/**
	 * Get following conversations
	 */
	getForYouFollowing: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>(
			'/conversation/for-you/following',
			{ params },
		),

	/**
	 * Get follow-up conversations
	 */
	getForYouFollowUp: (params: IPagination) =>
		apiClient.get<IPaginatedResponse<IConversation>>(
			'/conversation/for-you/follow-up',
			{ params },
		),

	/**
	 * Get conversation details
	 */
	getDetails: (conversationId: string) =>
		apiClient.get<IBaseResponse<IConversation>>(
			`/conversation/${conversationId}/details`,
		),

	/**
	 * Get conversation messages
	 */
	getMessages: (conversationId: string, params: IPagination) =>
		apiClient.get<IPaginatedResponse<IChatMessage>>(
			`/conversation/${conversationId}/messages`,
			{ params },
		),

	/**
	 * Send chat message
	 */
	chat: (data: IChat) =>
		apiClient.post<IBaseResponse<IChatMessage>>('/conversation/chat', data),

	/**
	 * Update conversation sharing status
	 */
	updateSharingStatus: (
		conversationId: string,
		sharingStatus: 'PRIVATE' | 'PUBLIC',
	) =>
		apiClient.post<IBaseResponse<void>>(
			`/conversation/${conversationId}/update-sharing-status`,
			{ sharingStatus },
		),

	/**
	 * Get conversation comments
	 */
	getComments: (conversationId: string, params: IPagination) =>
		apiClient.get<IPaginatedResponse<IComment>>(
			`/conversation/${conversationId}/comments`,
			{ params },
		),

	/**
	 * Add comment to conversation
	 */
	addComment: (conversationId: string, data: ICommentPayload) =>
		apiClient.post<IBaseResponse<IComment>>(
			`/conversation/${conversationId}/comment`,
			data,
		),

	/**
	 * React to conversation
	 */
	react: (conversationId: string, data: IReaction) =>
		apiClient.post<IBaseResponse<IReaction[]>>(
			`/conversation/${conversationId}/react`,
			data,
		),

	/**
	 * Get comment replies
	 */
	getCommentReplies: (commentId: number, params: IPagination) =>
		apiClient.get<IPaginatedResponse<IComment>>(
			`/conversation/comments/${commentId}/replies`,
			{ params },
		),

	/**
	 * React to comment
	 */
	reactToComment: (commentId: number, data: IReaction) =>
		apiClient.post<IBaseResponse<IReaction[]>>(
			`/conversation/comments/${commentId}/react`,
			data,
		),

	/**
	 * Get message tags
	 */
	getMessageTags: (messageId: number) =>
		apiClient.get<IBaseResponse<string[]>>(
			`/conversation/messages/${messageId}/tags`,
		),

	/**
	 * Get message content
	 */
	getMessageContent: (messageId: number) =>
		apiClient.get<string[]>(`/conversation/messages/${messageId}/content`),

	/**
	 * Provide feedback on message
	 */
	provideFeedback: (messageId: number, data: IFeedback) =>
		apiClient.post<IBaseResponse<IFeedback[]>>(
			`/conversation/messages/${messageId}/feedback`,
			data,
		),

	/**
	 * Follow up on conversation
	 */
	followUp: (data: IFollowUp) =>
		apiClient.post<IBaseResponse<string[]>>('/conversation/follow-up', data),
};
